import { useEffect } from 'react';
import { Navigate, Route, Routes, Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Home as HomeIcon, User } from 'lucide-react';
import { api, type MeResponse, type CoursesResponse } from '@/lib/api';
import { Logo } from '@/components/Logo';
import { CountdownTimer } from '@/components/CountdownTimer';
import Login from '@/pages/Login';
import Home from '@/pages/Home';
import Course from '@/pages/Course';
import Module from '@/pages/Module';
import Lesson from '@/pages/Lesson';
import Lesezeichen from '@/pages/Lesezeichen';
import Profil from '@/pages/Profil';
import { cn } from '@/lib/utils';

// react-router's <Navigate> only handles in-app routes; the legal pages live on
// the marketing domain, so replace the location to leave the SPA entirely.
function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/api/auth/me'),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  if (me.isLoading) return <div className="p-6 text-sm text-muted-foreground">Lädt …</div>;
  if (!me.data?.user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function TopHeader() {
  const loc = useLocation();
  const coursesQ = useQuery({
    queryKey: ['courses'],
    queryFn: () => api.get<CoursesResponse>('/api/courses'),
    staleTime: 60_000,
    retry: false,
  });

  if (loc.pathname === '/login' || loc.pathname === '/impressum' || loc.pathname === '/datenschutz') {
    return null;
  }

  // Soonest upcoming access expiry among courses the user can access. Drives
  // the header countdown (replaces the old upgrade CTA).
  const expiries = (coursesQ.data?.courses ?? [])
    .filter((c) => c.hasAccess && c.accessExpiresAt > 0)
    .map((c) => c.accessExpiresAt);
  const soonestExpiry = expiries.length > 0 ? Math.min(...expiries) : 0;

  return (
    <header
      className="sticky top-0 z-20 border-b border-border/70 bg-background/80 backdrop-blur-xl"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* Three columns rather than justify-between: the bar holds a logo and,
          sometimes, a countdown. Left-aligning a small mark in a wide empty bar
          reads as unfinished, and plain centering would shift the logo off
          centre whenever the countdown appears. The empty left cell balances
          the right one so the mark stays put either way. */}
      <div className="mx-auto grid max-w-3xl grid-cols-[1fr_auto_1fr] items-center gap-2 px-5 py-3.5 sm:px-6 sm:py-4">
        <div aria-hidden />
        <Link to="/" className="justify-self-center text-foreground">
          <Logo className="h-5 w-auto sm:h-6" />
        </Link>
        <div className="flex min-w-0 justify-end">
          {soonestExpiry > 0 && <CountdownTimer expiresAtSeconds={soonestExpiry} />}
        </div>
      </div>
    </header>
  );
}

function BottomNav() {
  const loc = useLocation();
  if (loc.pathname === '/login' || loc.pathname === '/impressum' || loc.pathname === '/datenschutz') {
    return null;
  }
  // Hide floating nav on lesson detail (it has its own prev/next bar)
  if (/^\/kurse\/[^/]+\/[^/]+\/[^/]+/.test(loc.pathname)) {
    return null;
  }
  const homeActive = loc.pathname === '/' || loc.pathname.startsWith('/kurse');
  const profileActive = loc.pathname.startsWith('/profil');
  // Icon stacked above label. The active state is a tinted wash rather than a
  // solid --primary fill: a saturated pill inside a pill read as a blob.
  const item = (active: boolean) =>
    cn(
      'flex min-w-[4.5rem] flex-col items-center gap-1 rounded-3xl px-4 py-2.5 text-[0.6875rem] font-medium leading-none transition-colors',
      active
        ? 'bg-primary/12 text-primary-strong'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
    );
  return (
    <nav
      className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
    >
      <div className="pointer-events-auto flex items-center gap-1 rounded-[1.75rem] border bg-background/85 p-2 shadow-lift ring-1 ring-foreground/5 backdrop-blur-xl">
        <Link to="/" className={item(homeActive)}>
          <HomeIcon className="size-5" />
          Kurse
        </Link>
        <Link to="/profil" className={item(profileActive)}>
          <User className="size-5" />
          Profil
        </Link>
      </div>
    </nav>
  );
}

export default function App() {
  return (
    <>
      <TopHeader />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/impressum"
          element={<ExternalRedirect to="https://go.fussbusiness.de/impressum/" />}
        />
        <Route
          path="/datenschutz"
          element={<ExternalRedirect to="https://go.fussbusiness.de/datenschutz/" />}
        />
        {/* Course-viewing routes REQUIRE a session. A funnel lead
            arrives with the soft /optin cookie already set, so /me resolves them
            and they pass straight through to the free course. A logged-out
            visitor (no cookie) is bounced to /login instead of being shown a
            page whose data API 401s into a "Fehler beim Laden." error. */}
        <Route path="/" element={<RequireAuth><Home /></RequireAuth>} />
        <Route path="/kurse/:slug" element={<RequireAuth><Course /></RequireAuth>} />
        <Route path="/kurse/:slug/:moduleId" element={<RequireAuth><Module /></RequireAuth>} />
        <Route
          path="/kurse/:slug/:moduleId/:lessonId"
          element={<RequireAuth><Lesson /></RequireAuth>}
        />
        <Route
          path="/lesezeichen"
          element={
            <RequireAuth>
              <Lesezeichen />
            </RequireAuth>
          }
        />
        <Route
          path="/profil"
          element={
            <RequireAuth>
              <Profil />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <BottomNav />
    </>
  );
}
